/**
 * glør Set in Warenkorb — legt mehrere Varianten per AJAX in den Warenkorb und
 * öffnet den Cart-Drawer.
 *
 * Robust gebaut:
 *  - Der Add (fetch /cart/add.js) läuft IMMER, unabhängig von allem anderen.
 *  - Das CartLinesUpdateEvent (für Cart-Item-Refresh) wird per DYNAMISCHEM Import
 *    von @shopify/events versucht — scheitert der Import, bricht der Rest nicht.
 *  - Der Drawer wird EXPLIZIT geöffnet (theme-drawer#cart-drawer.open()), damit er
 *    auch dann aufgeht, wenn das Theme-Setting "auto_open_cart_drawer" aus ist.
 */
class GlrCartSet extends HTMLElement {
  connectedCallback() {
    this.btn = this.querySelector('[data-glr-cart-set-add]');
    this.err = this.querySelector('[data-glr-cart-set-error]');
    this.items = this.#parse(this.dataset.items || '');
    this.discount = (this.dataset.discount || '').trim();
    if (this.btn) this.btn.addEventListener('click', () => this.#add());
  }

  /**
   * "id:menge" ODER "id:menge:sellingplan" (drittes Segment optional)
   * ODER Cart-Permalink (…/cart/ID:1,ID:1 — dort kein selling_plan).
   * @param {string} raw
   * @returns {{id:string, quantity:number, selling_plan:string}[]}
   */
  #parse(raw) {
    let str = (raw || '').trim();
    const idx = str.indexOf('/cart/');
    if (idx >= 0) str = str.slice(idx + 6);
    str = str.split('?')[0].trim();
    return str
      .split(',')
      .map((part) => {
        const [id, qty, plan] = part.split(':');
        return {
          id: (id || '').trim(),
          quantity: parseInt(qty, 10) || 1,
          selling_plan: (plan || '').trim(),
        };
      })
      .filter((it) => it.id);
  }

  #clearError() {
    if (this.err) {
      this.err.textContent = '';
      this.err.hidden = true;
    }
  }

  /** @param {string} msg */
  #showError(msg) {
    if (this.err) {
      this.err.textContent = msg;
      this.err.hidden = false;
    }
  }

  #openDrawer() {
    const drawer = document.getElementById('cart-drawer');
    if (drawer && typeof drawer.open === 'function') drawer.open();
  }

  async #add() {
    if (this.items.length === 0 || !this.btn || this.btn.classList.contains('is-loading')) return;
    this.btn.classList.add('is-loading');
    this.btn.setAttribute('aria-busy', 'true');
    this.#clearError();

    const sectionIds = Array.from(document.querySelectorAll('cart-items-component'))
      .map((el) => (el instanceof HTMLElement ? el.dataset.sectionId : null))
      .filter((id) => Boolean(id));

    /** @type {{items:Object[], sections?:string}} */
    const body = {
      items: this.items.map((i) => {
        const line = { id: i.id, quantity: i.quantity };
        if (i.selling_plan) line.selling_plan = i.selling_plan;
        return line;
      }),
    };
    if (sectionIds.length) body.sections = sectionIds.join(',');

    // Event best-effort: für den Cart-Item-Refresh im Drawer. Import fehlertolerant.
    let CartLinesUpdateEvent = null;
    let deferred = null;
    try {
      ({ CartLinesUpdateEvent } = await import('@shopify/events'));
      deferred = CartLinesUpdateEvent.createPromise();
      this.btn.dispatchEvent(
        new CartLinesUpdateEvent({
          action: 'add',
          context: 'product',
          lines: this.items.map((i) => ({ merchandiseId: i.id, quantity: i.quantity })),
          promise: deferred.promise,
        })
      );
    } catch (e) {
      /* @shopify/events nicht verfügbar — Add + Drawer-Open laufen trotzdem. */
    }

    try {
      const res = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
        body: JSON.stringify(body),
      });
      const data = await res.json();

      if (data.status) {
        if (deferred) deferred.reject?.(new Error(data.description || data.message || 'Add failed'));
        throw new Error(data.description || data.message || 'Add to cart failed');
      }

      // Optional: Rabattcode auf den Warenkorb anwenden (Theme-Weg: /cart/update.js).
      let discountSections = null;
      if (this.discount) {
        try {
          // WICHTIG: KEIN clear-then-apply und KEIN Waechter-Marker hier. Das
          // "Alle 3 Duefte"-Set taggt seine Positionen NICHT mit _glr_bundle.
          // Wuerde man vorher { discount: '' } setzen, wuerde man fremde bzw.
          // Subscription-Codes mit-leeren; wuerde man den Waechter-Marker setzen,
          // entfernte der Bundle-Rabatt-Waechter den Duft-Code sofort wieder
          // (weil keine _glr_bundle-Position im Warenkorb liegt). Also nur setzen.
          const body = { discount: this.discount };
          if (sectionIds.length) body.sections = sectionIds.join(',');
          const dres = await fetch(Theme.routes.cart_update_url, {
            method: 'POST',
            headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
            body: JSON.stringify(body),
          });
          const dj = await dres.json();
          const notApplicable = Array.isArray(dj.discount_codes)
            && dj.discount_codes.some((d) => d.code === this.discount && d.applicable === false);
          if (notApplicable) this.#showError(this.dataset.discountError || ('Rabattcode „' + this.discount + '" ist nicht gültig.'));
          else {
            // Post-Rabatt gerenderte Sektionen -> damit der Drawer den Rabatt sofort zeigt (ohne Reload).
            if (dj && dj.sections) discountSections = dj.sections;
          }
        } catch (e) {
          console.warn('[glr-cart-set] Rabattcode konnte nicht angewendet werden:', e);
        }
      }

      if (deferred && CartLinesUpdateEvent) {
        const cartRes = await fetch(`${Theme.routes.cart_url}.js`);
        const ajaxCart = await cartRes.json();
        deferred.resolve({
          cart: CartLinesUpdateEvent.createCartFromAjaxResponse(ajaxCart),
          detail: { items: ajaxCart.items, source: 'glr-cart-set', sections: discountSections || data.sections, didError: false },
        });
      }

      // Garantiert öffnen — unabhängig vom auto_open_cart_drawer-Setting.
      this.#openDrawer();
    } catch (err) {
      console.error('[glr-cart-set] Add to cart fehlgeschlagen:', err);
      this.#showError(err && err.message ? err.message : (this.dataset.errorText || 'Konnte nicht in den Warenkorb gelegt werden.'));
    } finally {
      this.btn.classList.remove('is-loading');
      this.btn.removeAttribute('aria-busy');
    }
  }
}

if (!customElements.get('glr-cart-set')) {
  customElements.define('glr-cart-set', GlrCartSet);
}
